import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

export type TableAlignment = "left" | "center" | "right";

const tableAlignmentDecoKey = new PluginKey("lumirTableAlignmentDeco");

/**
 * table 노드의 tableAlignment attr을 읽어 `data-table-alignment`를 노드 DOM에 입히는 데코레이션.
 *
 * 왜 renderHTML(글로벌 attr)만으로 부족한가: table 노드는 커스텀 nodeView
 * (BlockNoteTableView)를 사용하는데, 이 nodeView는 생성 시점의 HTMLAttributes만 wrapper div에
 * 적용하고 attr 변경 시 갱신하지 않는다. 그래서 setNodeMarkup으로 attr을 바꿔도 DOM에 반영되지
 * 않는다. Decoration.node는 PM이 매 state 변경마다 nodeView의 outer DOM에 속성을 적용하므로
 * 정렬 변경이 즉시 화면에 반영된다. (renderHTML attr은 HTML 내보내기/복사용으로 계속 유지.)
 */
function tableAlignmentDecorationPlugin(): Plugin {
  return new Plugin({
    key: tableAlignmentDecoKey,
    props: {
      decorations(state) {
        const decorations: Decoration[] = [];
        state.doc.descendants((node, pos) => {
          if (node.type.name === "table") {
            const align = node.attrs.tableAlignment;
            if (align && align !== "left") {
              decorations.push(
                Decoration.node(pos, pos + node.nodeSize, {
                  "data-table-alignment": align,
                }),
              );
            }
            return false; // 표 내부는 순회 불필요
          }
          return undefined;
        });
        return DecorationSet.create(state.doc, decorations);
      },
    },
  });
}

/**
 * 표(table) 블록 전체를 에디터 영역 기준으로 좌/가운데/우 정렬하는 Tiptap 확장.
 *
 * BlockNote 0.35는 테이블 블록 레벨 정렬을 모델링하지 않으므로, verticalAlignment/
 * rowHeight와 동일한 패턴으로 `table` 노드에 글로벌 attribute `tableAlignment`를 추가한다.
 *  - renderHTML이 `data-table-alignment`를 출력(BlockNoteTableView가 HTMLAttributes를
 *    적용하는 .bn-block-content[data-content-type=table] div에 붙음) → CSS가 content-폭
 *    <table>에 margin을 적용해 정렬한다(좌측은 기본이라 attr 미출력).
 *  - parseHTML로 복사/붙여넣기 라운드트립.
 *  - 저장(블록 JSON 주입)은 utils/table-vertical-alignment.ts의 injectTableBlockAttrs가 담당.
 *  - 로드는 BlockNote blockToNode가 block.props를 table 노드 attr로 펼쳐 자동 복원.
 */
export const TableAlignmentExtension = Extension.create({
  name: "tableAlignment",

  addGlobalAttributes() {
    return [
      {
        types: ["table"],
        attributes: {
          tableAlignment: {
            default: "left",
            parseHTML: (element) =>
              element.getAttribute("data-table-alignment") || "left",
            renderHTML: (attributes) => {
              if (
                !attributes.tableAlignment ||
                attributes.tableAlignment === "left"
              ) {
                return {};
              }
              return { "data-table-alignment": attributes.tableAlignment };
            },
          },
        },
      },
    ];
  },

  addProseMirrorPlugins() {
    return [tableAlignmentDecorationPlugin()];
  },
});
